import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle, Checkbox, Label, ModernButton, ModernInput, Toast, ToastType } from "../../components/ui"
import ConfirmDialog from "../../components/ui/ConfirmDialog"
import { apiService } from "../../services/api"
import type { SaleOperationalKanbanColumn } from "../../types"

type ChecklistTpl = { id: string; name: string; items: Array<{ id: string; title: string }> }
type PreparationAssignee = { id: string; name: string; email: string }

export const PreparationPipelineSettingsPage: React.FC = () => {
 const { t } = useTranslation()
 const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)
 const [columns, setColumns] = useState<SaleOperationalKanbanColumn[]>([])
 const [newColTitle, setNewColTitle] = useState("")
 const [columnToDelete, setColumnToDelete] = useState<SaleOperationalKanbanColumn | null>(null)
 const [columnChecklistDrafts, setColumnChecklistDrafts] = useState<Record<string, string[]>>({})
 const [columnResponsibleDrafts, setColumnResponsibleDrafts] = useState<Record<string, string>>({})
 const [savingResponsibleId, setSavingResponsibleId] = useState<string | null>(null)
 const [preparationAssignees, setPreparationAssignees] = useState<PreparationAssignee[]>([])
 const [templates, setTemplates] = useState<ChecklistTpl[]>([])
 const [tplName, setTplName] = useState("")
 const [tplItemLines, setTplItemLines] = useState<string[]>([""])
 const [expandedTplId, setExpandedTplId] = useState<string | null>(null)
 const [tplToDelete, setTplToDelete] = useState<ChecklistTpl | null>(null)

 const loadKanban = useCallback(async () => {
  try {
   const data = await apiService.getSaleKanbanColumns("preparation")
   const nextColumns = Array.isArray(data) ? data : []
   setColumns(nextColumns)
   setColumnResponsibleDrafts(Object.fromEntries(nextColumns.map((column) => [column.id, column.responsibleUserId ?? ""])))
  } catch {
   setToast({ message: t("settings.operationPipeline.kanbanLoadError"), type: "error" })
   setColumns([])
  }
 }, [t])

const loadPreparationAssignees = useCallback(async () => {
   try {
    let raw = await apiService.getPreparationAssignees()
    if (!Array.isArray(raw) || raw.length === 0) {
      const usersResponse = await apiService.getUsers(100, 1)
      raw = usersResponse?.users?.filter((u: any) => u.status === 'active' || u.isActive) || []
    }
    const normalized = Array.isArray(raw)
     ? raw.filter((user) => user?.id && user?.name).map((user) => ({ id: user.id, name: user.name, email: user.email }))
     : []
    setPreparationAssignees(normalized)
   } catch {
    setPreparationAssignees([])
   }
  }, [])

 const loadTemplates = useCallback(async () => {
  try {
   const data = await apiService.getSaleChecklistTemplates({ operationalStage: "preparation" })
   setTemplates(Array.isArray(data) ? data : [])
  } catch {
   setToast({ message: t("settings.operationPipeline.checklistLoadError"), type: "error" })
   setTemplates([])
  }
 }, [t])

 useEffect(() => { void loadKanban(); void loadPreparationAssignees(); void loadTemplates() }, [loadKanban, loadPreparationAssignees, loadTemplates])

 useEffect(() => {
  const mids = columns.slice(1, -1)
  if (mids.length === 0) return
  let cancelled = false
  void Promise.all(mids.map(async (c) => {
   try {
    const res = await apiService.getKanbanColumnChecklist(c.id)
    const titles = (res.items ?? []).map((i) => i.title)
    return [c.id, titles.length > 0 ? titles : [""]] as const
   } catch {
    return [c.id, [""]] as const
   }
  })).then((pairs) => { if (!cancelled) setColumnChecklistDrafts(Object.fromEntries(pairs)) })
  return () => { cancelled = true }
 }, [columns])

 const patchColumn = async (
  columnId: string,
  patch: { title?: string; isActive?: boolean; isHandoffColumn?: boolean; responsibleUserId?: string | null }
 ) => {
  try {
   await apiService.updateSaleKanbanColumn(columnId, patch)
   await loadKanban()
   return true
  } catch {
   setToast({ message: t("settings.operationPipeline.columnSaveError"), type: "error" })
   return false
  }
 }

 const saveResponsible = async (columnId: string) => {
  try {
   setSavingResponsibleId(columnId)
   const value = columnResponsibleDrafts[columnId] ?? ""
   const ok = await patchColumn(columnId, { responsibleUserId: value || null })
   if (ok) {
    setToast({ message: t("settings.operationPipeline.columnCreated"), type: "success" })
   }
  } finally { setSavingResponsibleId(null) }
 }

 const addColumn = async () => {
  const title = newColTitle.trim()
  if (!title) return
  try { await apiService.createSaleKanbanColumn({ operationalStage: "preparation", title }); setNewColTitle(""); await loadKanban() } catch { setToast({ message: t("settings.operationPipeline.columnSaveError"), type: "error" }) }
 }

 const saveColumnChecklistLines = async (columnId: string) => {
  const lines = (columnChecklistDrafts[columnId] ?? []).map((s) => s.trim()).filter(Boolean)
  try {
   await apiService.putKanbanColumnChecklist(columnId, lines.map((title, i) => ({ title, sortOrder: i, required: true })))
   setToast({ message: t("settings.operationPipeline.columnChecklistSaved"), type: "success" })
  } catch { setToast({ message: t("settings.operationPipeline.columnChecklistError"), type: "error" }) }
 }

 const createTemplate = async () => {
  const name = tplName.trim()
  const lines = tplItemLines.map((s) => s.trim()).filter(Boolean)
  if (!name || lines.length === 0) return
  try {
   await apiService.createSaleChecklistTemplate({ operationalStage: "preparation", name, items: lines.map((title, idx) => ({ title, sortOrder: idx, required: true })) })
   setTplName("")
   setTplItemLines([""])
   await loadTemplates()
  } catch { setToast({ message: t("settings.operationPipeline.templateSaveError"), type: "error" }) }
 }

 return (
  <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
   {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
   <div>
    <h1 className="text-2xl font-bold text-foreground">{t("settings.operationPipeline.pageTitle")}</h1>
    <p className="text-sm text-muted-foreground mt-1">{t("settings.operationPipeline.pageSubtitle")}</p>
   </div>
   <Card>
    <CardHeader><CardTitle className="text-base">{t("settings.operationPipeline.kanbanTitle")}</CardTitle></CardHeader>
    <CardContent className="space-y-4">
     {columns.map((col, idx) => {
      const isEdge = idx === 0 || idx === columns.length - 1
      return (
       <div key={col.id} className="space-y-3 rounded-lg border border-border p-3">
        <ModernInput value={col.title} disabled={isEdge} onChange={(e) => setColumns((prev) => prev.map((item) => (item.id === col.id ? { ...item, title: e.target.value } : item)))} onBlur={() => !isEdge && void patchColumn(col.id, { title: col.title.trim() })} />
        {idx !== 0 ? (
         <div className="grid gap-2 md:grid-cols-[1fr_auto] md:items-end">
          <div>
           <Label>{t("settings.operationPipeline.columnResponsible")}</Label>
           <select className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={columnResponsibleDrafts[col.id] ?? ""} onChange={(e) => setColumnResponsibleDrafts((prev) => ({ ...prev, [col.id]: e.target.value }))}>
            <option value="">{t("settings.operationPipeline.columnResponsibleEmpty")}</option>
            {preparationAssignees.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
           </select>
          </div>
          <ModernButton type="button" size="sm" disabled={savingResponsibleId === col.id} onClick={() => void saveResponsible(col.id)}>{t("actions.save")}</ModernButton>
         </div>
        ) : null}
        {!isEdge ? (
         <div className="space-y-2 border-t border-border pt-3">
          {(columnChecklistDrafts[col.id] ?? [""]).map((line, lineIdx) => (
           <div key={`${col.id}-${lineIdx}`} className="flex items-start gap-2">
            <Checkbox disabled checked={false} className="mt-2 opacity-40 pointer-events-none" />
            <ModernInput value={line} onChange={(e) => setColumnChecklistDrafts((prev) => { const next = [...(prev[col.id] ?? [""])]; next[lineIdx] = e.target.value; return { ...prev, [col.id]: next } })} />
            <ModernButton type="button" variant="outline" size="sm" onClick={() => setColumnChecklistDrafts((prev) => { const next = [...(prev[col.id] ?? [""])]; next.splice(lineIdx, 1); return { ...prev, [col.id]: next.length ? next : [""] } })}><Trash2 className="h-4 w-4" /></ModernButton>
           </div>
          ))}
          <div className="flex gap-2">
           <ModernButton type="button" variant="outline" size="sm" onClick={() => setColumnChecklistDrafts((prev) => ({ ...prev, [col.id]: [...(prev[col.id] ?? []), ""] }))}><Plus className="h-4 w-4 mr-2" />{t("settings.operationPipeline.addChecklistItem")}</ModernButton>
           <ModernButton type="button" size="sm" onClick={() => void saveColumnChecklistLines(col.id)}>{t("settings.operationPipeline.saveColumnChecklist")}</ModernButton>
          </div>
         </div>
        ) : null}
       </div>
      )
     })}
     <div className="flex gap-2 border-t border-border pt-3">
      <ModernInput value={newColTitle} onChange={(e) => setNewColTitle(e.target.value)} placeholder={t("settings.operationPipeline.newColumnPlaceholder")} />
      <ModernButton type="button" onClick={() => void addColumn()}><Plus className="h-4 w-4 mr-2" />{t("settings.operationPipeline.addColumn")}</ModernButton>
     </div>
    </CardContent>
   </Card>
   <Card>
    <CardHeader><CardTitle className="text-base">{t("settings.operationPipeline.checklistsTitle")}</CardTitle></CardHeader>
    <CardContent className="space-y-3">
     {templates.map((tpl) => (
      <div key={tpl.id} className="rounded-lg border border-border p-3 space-y-2">
       <div className="flex items-center justify-between gap-2">
        <button type="button" className="flex items-center gap-2 text-left" onClick={() => setExpandedTplId((id) => (id === tpl.id ? null : tpl.id))}>
         {expandedTplId === tpl.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
         <span className="font-medium">{tpl.name}</span>
        </button>
        <ModernButton type="button" variant="outline" size="sm" className="text-destructive" onClick={() => setTplToDelete(tpl)}><Trash2 className="h-4 w-4" /></ModernButton>
       </div>
       {expandedTplId === tpl.id ? <div className="space-y-1 pl-6">{tpl.items.map((item) => <p key={item.id} className="text-sm">{item.title}</p>)}</div> : null}
      </div>
     ))}
     <div className="space-y-2 border-t border-border pt-3">
      <Label>{t("settings.operationPipeline.newTemplate")}</Label>
      <ModernInput value={tplName} onChange={(e) => setTplName(e.target.value)} />
      {tplItemLines.map((line, idx) => <ModernInput key={`tpl-line-${idx}`} value={line} onChange={(e) => setTplItemLines((prev) => prev.map((item, lineIdx) => (lineIdx === idx ? e.target.value : item)))} />)}
      <div className="flex gap-2">
       <ModernButton type="button" variant="outline" size="sm" onClick={() => setTplItemLines((prev) => [...prev, ""])}><Plus className="h-4 w-4 mr-2" />{t("settings.operationPipeline.addChecklistItem")}</ModernButton>
       <ModernButton type="button" onClick={() => void createTemplate()}>{t("settings.operationPipeline.createTemplate")}</ModernButton>
      </div>
     </div>
    </CardContent>
   </Card>
   <ConfirmDialog isOpen={!!columnToDelete} onClose={() => setColumnToDelete(null)} title={t("settings.operationPipeline.confirmDeleteColumn")} description={t("settings.operationPipeline.confirmDeleteColumnHint")} confirmText={t("actions.delete")} cancelText={t("actions.cancel")} type="danger" onConfirm={() => { if (!columnToDelete) return; void apiService.deleteSaleKanbanColumn(columnToDelete.id).then(() => { setColumnToDelete(null); void loadKanban() }) }} />
   <ConfirmDialog isOpen={!!tplToDelete} onClose={() => setTplToDelete(null)} title={t("settings.operationPipeline.confirmDeleteTemplate")} description={t("settings.operationPipeline.confirmDeleteTemplateHint")} confirmText={t("actions.delete")} cancelText={t("actions.cancel")} type="danger" onConfirm={() => { if (!tplToDelete) return; void apiService.deleteSaleChecklistTemplate(tplToDelete.id).then(() => { setTplToDelete(null); void loadTemplates() }) }} />
  </div>
 )
}
